// @ts-check
/**
 * SH-01: one router. Routes follow the navigation map (plan §4.2) under `#/…`.
 * Legacy hashes (`#manage/*`, `#settings/user/*`, `#bot/*`, `#project/*`) parse to
 * the same routes and are rewritten to the canonical form in place.
 *
 * @typedef {{ name: "home" } |
 *   { name: "page", page: string, tab?: string, id?: string, item?: string } |
 *   { name: "settings", scope: "team" | "user", section?: string } |
 *   { name: "bunny", agentId: string, projectId?: string, conversationId?: string } |
 *   { name: "project", projectId: string, view?: string } |
 *   { name: "unknown" }} Route
 */

const id = "([a-f0-9-]+)";
/**
 * Management pages and their canonical paths. ADM-02: a create page is
 * `<list>/new`; a page for one record ends in `/:id` and carries `route.id`.
 * Pages under one record name it with `:id` and their own record with
 * `:item` (`bunnies/:id/memory/:item`).
 */
const pages = /** @type {const} */ ({
  overview: "settings/team/overview",
  attention: "needs-you",
  work: "work",
  "workspace-file-export": "work/:id/files/:item/export",
  operations: "automations",
  connections: "connections",
  bunnies: "bunnies",
  "new-bunny": "bunnies/new",
  // EXT-01: `#/bunnies/pair/<code>` opens it with the code filled in.
  "pair-agent": "bunnies/pair",
  // BOT-01..02: import a configuration export; clone; save as template.
  "import-bunny": "bunnies/import",
  "bunny-clone": "bunnies/:id/clone",
  "bunny-template": "bunnies/:id/template",
  "new-bunny-memory": "bunnies/:id/memory/new",
  "bunny-memory": "bunnies/:id/memory/:item",
  "new-bunny-skill": "bunnies/:id/skills/new",
  "import-bunny-skill": "bunnies/:id/skills/import",
  "bunny-skill": "bunnies/:id/skills/:item",
  "new-automation": "automations/new",
  "new-monitor": "automations/monitors/new",
  "new-package": "automations/packages/new",
  "automation-package": "automations/packages/:id",
  "new-task": "automations/tasks/new",
  "new-skill": "skills/new",
  // LEARN-01..03: a skill's page, a new evaluation case and a skill draft.
  "skill-detail": "skills/library/:id",
  "new-skill-evaluation": "skills/library/:id/evaluations/new",
  "skill-draft": "skills/drafts/:id",
  "new-note": "knowledge/new",
  "new-connection-target": "connections/targets/new",
  "catalog-entry": "connections/browse/:id",
  "catalog-add": "connections/browse/:id/add",
  "workspace-account": "connections/workspace/:id",
  "new-workspace-account": "connections/workspace/new",
  "new-workspace-request": "connections/workspace/requests/new",
  "workspace-request": "connections/workspace/requests/:id",
  "account-review": "connections/setup/new",
  "account-setup": "connections/setup/:id",
  "connection-target": "connections/targets/:id",
  "new-connection-secret": "connections/secrets/new",
  "connection-secret": "connections/secrets/:id",
  "new-connection-grant": "connections/access/new",
  // SHIP-01: a repository's page and its "+ New remote" page.
  repository: "connections/repositories/:id",
  "new-repository-remote": "connections/repositories/:id/remotes/new",
  // ENG-L3: a coding node's page.
  "remote-node": "connections/nodes/:id",
  // HOOK-02: a signed forge webhook for one of the repository's remotes.
  "new-forge-webhook": "connections/repositories/:id/webhooks/new",
  // HOOK-03: setup recipes: import one, then use it.
  "new-setup-recipe": "connections/recipes/new",
  "setup-recipe": "connections/recipes/:id",
  // HOOK-01/02: a webhook automation's filters and a new trigger.
  "automation-filters": "automations/:id/filters",
  "new-automation-trigger": "automations/:id/triggers/new",
  "work-access": "work/access/:id",
  "new-messaging-channel": "settings/team/messaging/new",
  "messaging-channel": "settings/team/messaging/:id",
  "new-member-invite": "settings/team/team/new",
  "new-local-user": "settings/team/team/users/new",
  "new-team": "settings/team/team/teams/new",
  "new-model-provider": "settings/team/models/new",
  "model-provider": "settings/team/models/:id",
  // OPS-03: a spend budget's create and edit pages.
  "new-spend-budget": "settings/team/spending/new",
  "spend-budget": "settings/team/spending/:id",
  "new-assistant-account": "settings/me/integrations/new",
  "assistant-account": "settings/me/integrations/:id",
  "new-media-profile": "settings/me/media/new",
  // BRW-01: a browser profile and its create page.
  "new-browser-profile": "settings/me/browsing/new",
  "browser-profile": "settings/me/browsing/:id",
  // PUB-01: a publication (its manifest to review) and its create page.
  "new-publication": "settings/team/publications/new",
  publication: "settings/team/publications/:id",
});
/** Pages whose tabs appear in the URL. */
const tabbed = new Set(["operations", "connections", "skills"]);
/** @type {Map<string, string>} */
const pageByPath = new Map(
  Object.entries(pages).map(([page, path]) => [path, page]),
);
/**
 * List pages that also show one record in place: `#/bunnies/<id>` is the
 * Bunnies page showing that bunny (`route.id`).
 */
const listRecords = new Set(["bunnies", "pair-agent"]);
const segment = "([a-z0-9-]+)";
/**
 * Create pages and record pages, compiled from their paths. Fewer parameters
 * match first, so `bunnies/new` wins over `bunnies/:id` and
 * `bunnies/:id/skills/new` over `bunnies/:id/skills/:item`.
 * @type {[RegExp, (match: RegExpExecArray) => Route][]}
 */
const paramPages = Object.entries(pages)
  .filter(
    ([, path]) =>
      path.startsWith("settings/") ||
      path.endsWith("/new") ||
      path.endsWith("/pair") ||
      // BOT-02: `bunnies/import` is a page, not the bunny with id "import".
      path.endsWith("/import") ||
      path.includes(":"),
  )
  .map(([page, path]) => {
    const names = [...path.matchAll(/:(id|item)/gu)].map((m) => m[1]);
    const pattern = new RegExp(
      `^#/${path.replace(/:(?:id|item)/gu, segment)}$`,
      "i",
    );
    return /** @type {const} */ ([names.length, pattern, page, names]);
  })
  .sort((a, b) => a[0] - b[0])
  .map(
    ([, pattern, page, names]) =>
      /** @type {[RegExp, (match: RegExpExecArray) => Route]} */ ([
        pattern,
        (m) => ({
          name: "page",
          page,
          ...Object.fromEntries(
            names.map((name, index) => [name, m[index + 1]]),
          ),
        }),
      ]),
  );

/** @type {[RegExp, (match: RegExpExecArray) => Route][]} */
const patterns = [
  [/^#?\/?$/, () => ({ name: "home" })],
  // ADM-02: create pages (`#/automations/new`) and record pages come before
  // the tab and section forms that would otherwise read `new` as a tab.
  ...paramPages,
  ...[...listRecords].map(
    (page) =>
      /** @type {[RegExp, (match: RegExpExecArray) => Route]} */ ([
        new RegExp(
          `^#/${pages[/** @type {keyof typeof pages} */ (page)]}/${segment}$`,
          "i",
        ),
        (m) => ({ name: "page", page, id: m[1] }),
      ]),
  ),
  [
    new RegExp(`^#/bunny/${id}(?:/conversation/${id})?$`, "i"),
    (m) => bunny(m[1], undefined, m[2]),
  ],
  [
    new RegExp(`^#/project/${id}/bunny/${id}(?:/conversation/${id})?$`, "i"),
    (m) => bunny(m[2], m[1], m[3]),
  ],
  [
    /^#\/project\/([a-f0-9-]+|new)$/i,
    (m) => ({ name: "project", projectId: m[1] }),
  ],
  // ADM-02: a project's create and edit pages (`#/project/<id>/tasks/new`,
  // `/settings`, `/members/<bunny id>`). Project chats are matched above.
  [
    /^#\/project\/([a-z0-9-]+)\/(settings|(?:members|tasks|goals|workflows|notes|peers|checkpoint|discussions)\/[a-z0-9-]+|collections\/[a-z0-9-]+(?:\/records\/[a-z0-9-]+)?)$/i,
    (m) => ({ name: "project", projectId: m[1], view: m[2] }),
  ],
  // Knowledge moved out of Personal settings (OPS-06).
  [
    /^#\/?settings\/(?:me|user)\/knowledge$/,
    () => ({ name: "page", page: "knowledge" }),
  ],
  [
    /^#\/settings\/(team|me)(?:\/([a-z]+))?$/,
    (m) => settings(m[1] === "me" ? "user" : "team", m[2]),
  ],
  [
    /^#\/automations(?:\/([a-z]+))?$/,
    (m) => ({ name: "page", page: "operations", ...(m[1] && { tab: m[1] }) }),
  ],
  [
    /^#\/(connections|skills)\/([a-z]+)$/,
    (m) => ({ name: "page", page: m[1], tab: m[2] }),
  ],
  [
    /^#\/([a-z-]+(?:\/new)?)$/,
    (m) => ({ name: "page", page: pageByPath.get(m[1]) || m[1] }),
  ],
  // Legacy forms.
  [
    new RegExp(`^#bot/${id}(?:/conversation/${id})?$`, "i"),
    (m) => bunny(m[1], undefined, m[2]),
  ],
  [
    new RegExp(`^#project/${id}/bot/${id}(?:/conversation/${id})?$`, "i"),
    (m) => bunny(m[2], m[1], m[3]),
  ],
  [
    /^#project\/([a-f0-9-]+|new)$/i,
    (m) => ({ name: "project", projectId: m[1] }),
  ],
  [
    /^#settings\/(user|team)(?:\/([a-z]+))?$/,
    (m) => settings(/** @type {"user" | "team"} */ (m[1]), m[2]),
  ],
  [/^#manage\/([a-z-]+)$/, (m) => ({ name: "page", page: m[1] })],
];

/** @param {string} agentId @param {string} [projectId] @param {string} [conversationId] @returns {Route} */
function bunny(agentId, projectId, conversationId) {
  return {
    name: "bunny",
    agentId,
    ...(projectId && { projectId }),
    ...(conversationId && { conversationId }),
  };
}
/** @param {"team" | "user"} scope @param {string} [section] @returns {Route} */
function settings(scope, section) {
  return { name: "settings", scope, ...(section && { section }) };
}

/** @param {string} hash @returns {Route} */
export function parseRoute(hash = "") {
  for (const [pattern, build] of patterns) {
    const match = pattern.exec(hash);
    if (match) return build(match);
  }
  return { name: "unknown" };
}

/** Canonical hash for a route ("" for home). @param {Route} route */
export function routeHash(route) {
  switch (route.name) {
    case "home":
      return "";
    case "bunny":
      return `#/${route.projectId ? `project/${route.projectId}/` : ""}bunny/${route.agentId}${route.conversationId ? `/conversation/${route.conversationId}` : ""}`;
    case "project":
      return `#/project/${route.projectId}${route.view ? `/${route.view}` : ""}`;
    case "settings":
      return `#/settings/${route.scope === "user" ? "me" : "team"}${route.section ? `/${route.section}` : ""}`;
    case "page": {
      const path = /** @type {string} */ (
        pages[/** @type {keyof typeof pages} */ (route.page)] || route.page
      );
      if (path.includes(":"))
        return `#/${path
          .replace(":id", encodeURIComponent(route.id || ""))
          .replace(":item", encodeURIComponent(route.item || ""))}`;
      if (listRecords.has(route.page) && route.id)
        return `#/${path}/${encodeURIComponent(route.id)}`;
      return `#/${path}${tabbed.has(route.page) && route.tab ? `/${route.tab}` : ""}`;
    }
    default:
      return "";
  }
}

/** @param {Route} route @param {string} name @param {string} [page] */
export function isRoute(route, name, page) {
  return (
    route.name === name &&
    (page === undefined || (route.name === "page" && route.page === page))
  );
}

/**
 * @typedef {(route: Route, change: { source: "history" | "navigate" }) => void} Listener
 * @param {{ window?: any, location?: any, history?: any }} [env]
 */
export function createRouter(env = {}) {
  const win = () => env.window || globalThis.window;
  const loc = () => env.location || globalThis.location;
  const hist = () => env.history || globalThis.history;
  /** @type {Listener[]} */
  const listeners = [];
  const base = () => `${loc().pathname || ""}${loc().search || ""}`;
  // A bare hash keeps the path; home drops the hash by naming the path.
  const url = (/** @type {string} */ hash) => hash || base();
  const currentHash = () => (loc().hash === "#" ? "" : loc().hash || "");
  /** Rewrite a legacy or non-canonical hash without adding a history entry. */
  function canonicalize() {
    const route = parseRoute(currentHash());
    if (route.name === "unknown") return route;
    const hash = routeHash(route);
    if (hash !== currentHash())
      hist().replaceState(hist().state, "", url(hash));
    return route;
  }
  /** @param {"history" | "navigate"} source */
  function emit(source) {
    listen();
    const route = parseRoute(currentHash());
    for (const listener of [...listeners]) listener(route, { source });
  }
  let listening = false;
  function listen() {
    const target = win();
    if (listening || !target?.addEventListener) return;
    listening = true;
    const onHistory = () => {
      canonicalize();
      emit("history");
    };
    target.addEventListener("hashchange", onHistory);
    target.addEventListener("popstate", onHistory);
  }
  return {
    get current() {
      return parseRoute(currentHash());
    },
    canonicalize,
    /**
     * Subscribe to route changes: in-app navigation and refreshes (`source:
     * "navigate"`) and back/forward or typed URLs (`source: "history"`).
     * Listeners run in subscription order.
     * @param {Listener} listener
     */
    subscribe(listener) {
      listen();
      listeners.push(listener);
      return () => {
        listeners.splice(listeners.indexOf(listener), 1);
      };
    },
    /**
     * Go to a route (object or hash, legacy forms accepted). Pushes a history
     * entry unless `replace`; `silent` updates the URL without notifying.
     * @param {Route | string} target @param {{ replace?: boolean, silent?: boolean }} [options]
     */
    navigate(target, { replace = false, silent = false } = {}) {
      const route = typeof target === "string" ? parseRoute(target) : target;
      const hash =
        route.name === "unknown" && typeof target === "string"
          ? target
          : routeHash(route);
      if (hash !== currentHash()) {
        if (replace) hist().replaceState(hist().state, "", url(hash));
        else hist().pushState(null, "", url(hash));
      }
      if (!silent) emit("navigate");
    },
    /** Re-run listeners for the current URL (after permissions or data change). */
    refresh() {
      emit("navigate");
    },
  };
}

export const router = createRouter();
